import React, { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import { Grid, Row, GridContainer } from '@xingternal/xing-grid';
import { Headline } from '@xingternal/typography';
import { useServerData } from '@xingternal/hops';
import { Dialog } from '@xingternal/dialog';

import { useXingFrame } from '@/hooks/useXingFrame';
import { trackingParams } from '@/utils/tracking';
import { ROUTES } from '@/routes';

import LinkBar, { LinkBarNavigationProps } from '@/components/LinkBar';

import styles from './LoggedInPage.css';

import { LoggedInHeader } from './LoggedInHeader';

type LoggedInContentProps = {
  children: React.ReactNode;
};
export const LoggedInContent: FC<LoggedInContentProps> = ({ children }) => {
  const { formatMessage } = useIntl();
  const { search, pathname } = useLocation();
  const {
    features: { isBrandManagerEditor },
  } = useXingFrame();
  const { isWebview } = useServerData();

  const isRedirectedFromOnlyfy =
    new URLSearchParams(search).get('onlyfyLogin') === 'true';

  const [showRedirectDialog, setShowRedirectDialog] = useState<boolean>(false);

  useEffect(() => {
    setShowRedirectDialog(isRedirectedFromOnlyfy);
  }, [isRedirectedFromOnlyfy]);

  const hasToShowLinkBar = !(
    pathname.includes(ROUTES.industry) ||
    pathname.includes(ROUTES.industries) ||
    pathname.includes(ROUTES.mostVisitedCompanies) ||
    pathname.includes(ROUTES.recentlyClaimedCompanies)
  );

  const navigation: Array<LinkBarNavigationProps> = [
    {
      to: ROUTES.companies,
      trackingAttributes: trackingParams.navigationRecommended,
      copy: formatMessage({ id: 'CP_LANDING_PAGE_RECOMMENDED_LINK' }),
    },
    {
      to: ROUTES.managedCompanies,
      trackingAttributes: trackingParams.navigationManaging,
      copy: formatMessage({ id: 'CP_100_MY_EDITED_COMPANIES_TITLE' }),
    },
    {
      to: ROUTES.followedCompanies,
      trackingAttributes: trackingParams.navigationFollowing,
      copy: formatMessage({ id: 'CP_FOLLOWING_COMPANIES' }),
    },
  ];

  return (
    <>
      <Dialog
        confirmLabel={formatMessage({
          id: 'CP_REDIRECT_PERMISSIONS_DIALOG_BUTTON',
        })}
        onConfirm={() => setShowRedirectDialog(false)}
        headline={formatMessage({
          id: 'CP_REDIRECT_PERMISSIONS_DIALOG_TITLE',
        })}
        text={formatMessage({
          id: 'CP_REDIRECT_PERMISSIONS_DIALOG_MESSAGE',
        })}
        show={showRedirectDialog}
      />
      <GridContainer type="standard">
        <Grid>
          {!isWebview && (
            <LoggedInHeader
              showBackToCompanies={!hasToShowLinkBar}
              isBrandManagerEditor={isBrandManagerEditor as boolean}
            />
          )}
          <div
            className={styles.mobileBanner}
            dangerouslySetInnerHTML={{
              __html: formatMessage({
                id: 'CP_LANDING_MOBILE_BANNER',
              }),
            }}
          />

          {hasToShowLinkBar && (
            <>
              <Row>
                <Headline size="xxlarge" as="h1" className={styles.headline}>
                  <FormattedMessage id="SE_LABEL_COMPANIES" />
                </Headline>
              </Row>
              <Row>
                <LinkBar navigation={navigation} />
              </Row>
            </>
          )}
          <Row>{children}</Row>
        </Grid>
      </GridContainer>
    </>
  );
};
